import React, { useState } from 'react';
import { object, string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { getSavedSearches, removeSavedSearch, buildSearchUrl } from './savedSearchUtils';
import css from './SavedSearchesPanel.module.css';

/**
 * SavedSearchesPanel — shows the user's saved searches.
 *
 * Renders a list of saved searches with "Run search" links and "Delete" buttons.
 * Integrate into AccountSettingsPage or a dedicated /saved-searches page.
 *
 * Usage:
 *   <SavedSearchesPanel
 *     currentUser={currentUser}
 *     onDelete={async (updatedProtectedData) => {
 *       await dispatch(updateProfile({ protectedData: updatedProtectedData }));
 *     }}
 *   />
 */
const SavedSearchesPanel = ({ currentUser, onDelete, className }) => {
  const searches = getSavedSearches(currentUser);
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (searchId) => {
    if (deleting) return;
    setDeleting(searchId);
    try {
      const updated = removeSavedSearch(currentUser, searchId);
      if (onDelete) await onDelete(updated);
    } finally {
      setDeleting(null);
    }
  };

  if (searches.length === 0) {
    return (
      <div className={classNames(css.root, className)}>
        <p className={css.empty}>
          <FormattedMessage id="SavedSearchesPanel.empty" />
        </p>
      </div>
    );
  }

  return (
    <div className={classNames(css.root, className)}>
      <h3 className={css.title}>
        <FormattedMessage id="SavedSearchesPanel.title" />
      </h3>
      <ul className={css.list}>
        {searches.map(search => (
          <li key={search.id} className={css.item}>
            <div className={css.itemInfo}>
              <span className={css.name}>{search.name}</span>
              <span className={css.date}>
                {new Date(search.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className={css.actions}>
              <a href={buildSearchUrl(search.query)} className={css.runLink}>
                <FormattedMessage id="SavedSearchesPanel.run" />
              </a>
              <button
                type="button"
                onClick={() => handleDelete(search.id)}
                disabled={deleting === search.id}
                className={css.deleteBtn}
              >
                <FormattedMessage id="SavedSearchesPanel.delete" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

SavedSearchesPanel.defaultProps = { className: null, onDelete: null };
SavedSearchesPanel.propTypes = { currentUser: object, onDelete: func, className: string };

export default SavedSearchesPanel;
